import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  UtensilsCrossed, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Leaf,
  Apple,
  ChefHat,
  TrendingUp,
  Target,
  Users,
  Salad
} from 'lucide-react';

export default function MarchesPublicsRestaurationPage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Restauration Collective : Appels d'Offres Cantines et Traiteurs"
        description="Comment trouver des marchés publics de restauration collective. Guide complet : cantines scolaires, restauration hospitalière, repas EHPAD. Conseils pour traiteurs et cuisines centrales."
        path="/marches-publics-restauration"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics restauration' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-900 via-emerald-900 to-slate-900 py-16 text-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 rounded-full text-sm font-medium mb-6">
                <UtensilsCrossed className="w-4 h-4" />
                Secteur Restauration Collective
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marchés Publics Restauration : Appels d'Offres Cantines et Services de Repas
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Écoles, hôpitaux, EHPAD, administrations... La restauration collective publique représente 
                un marché colossal en pleine transformation vers le bio et le local.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-green-600 hover:bg-green-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés restauration
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '9 Md€', label: 'restauration collective/an', icon: TrendingUp },
                { value: '50%', label: 'bio/local d\'ici 2025 (loi EGalim)', icon: Leaf },
                { value: '6000+', label: 'marchés restauration/an', icon: FileText },
                { value: '3,5 Md', label: 'repas servis par an', icon: Users }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-green-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <UtensilsCrossed className="w-8 h-8 text-green-600" />
                Quels types de marchés publics de restauration ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de restauration collective</strong> couvrent la préparation et la 
                  livraison de repas pour les établissements publics. La loi EGalim transforme profondément 
                  ce secteur avec des exigences croissantes en produits bio, locaux et durables.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {[
                  {
                    icon: Apple,
                    title: 'Restauration scolaire',
                    items: ['Cantines écoles primaires', 'Collèges et lycées', 'Crèches et périscolaire', 'Centres de loisirs']
                  },
                  {
                    icon: Users,
                    title: 'Restauration sanitaire',
                    items: ['Hôpitaux et cliniques', 'EHPAD et maisons de retraite', 'Centres de rééducation', 'Portage de repas à domicile']
                  },
                  {
                    icon: ChefHat,
                    title: 'Services de traiteur',
                    items: ['Cocktails et réceptions', 'Séminaires et conférences', 'Événements officiels', 'Plateaux-repas']
                  },
                  {
                    icon: Salad,
                    title: 'Fournitures alimentaires',
                    items: ['Produits frais', 'Produits bio et locaux', 'Surgelés et conserves', 'Pain et viennoiseries']
                  }
                ].map((category, i) => (
                  <div key={`item-${i}`} className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <category.icon className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Loi EGalim Box */}
            <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
              <div className="flex gap-4">
                <Leaf className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2 text-lg">Loi EGalim : une opportunité pour les producteurs locaux</h3>
                  <p className="text-slate-600 mb-4">
                    Depuis 2022, les cantines scolaires doivent proposer au moins <strong>50% de produits durables 
                    et de qualité</strong>, dont <strong>20% de bio</strong>. Cette réglementation crée de nouvelles 
                    opportunités pour les producteurs locaux et les traiteurs engagés.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { label: 'Produits bio', value: '20% minimum' },
                      { label: 'Produits durables/locaux', value: '50% minimum' },
                      { label: 'Menu végétarien', value: '1x par semaine' }
                    ].map((item, i) => (
                      <div key={`item-${i}`} className="bg-white rounded-lg p-3 text-center">
                        <div className="text-green-600 font-bold">{item.value}</div>
                        <div className="text-sm text-slate-500">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Target className="w-8 h-8 text-green-600" />
                Qui sont les acheteurs publics en restauration ?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: 'Communes & EPCI',
                    description: 'Cantines scolaires, crèches, centres de loisirs, CCAS. Premier marché en volume.',
                    volume: '55% des marchés'
                  },
                  {
                    title: 'Établissements de santé',
                    description: 'Hôpitaux, EHPAD, centres médico-sociaux. Exigences nutritionnelles spécifiques.',
                    volume: '30% des marchés'
                  },
                  {
                    title: 'Régions & Départements',
                    description: 'Restauration des lycées et collèges. Marchés d\'envergure régionale.',
                    volume: '15% des marchés'
                  }
                ].map((buyer, i) => (
                  <div key={`item-${i}`} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                    <h3 className="font-semibold text-slate-900 mb-2">{buyer.title}</h3>
                    <p className="text-slate-600 text-sm mb-3">{buyer.description}</p>
                    <div className="text-green-600 font-medium">{buyer.volume}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 - Conseils */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <ChefHat className="w-8 h-8 text-green-600" />
                Réussir vos marchés de restauration
              </h2>
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Points clés du mémoire technique</h3>
                    <ul className="space-y-3">
                      {[
                        'Plan alimentaire et variété des menus',
                        'Approvisionnement local et circuits courts',
                        'Traçabilité et sécurité alimentaire (HACCP)',
                        'Gestion des régimes spéciaux et allergies',
                        'Lutte contre le gaspillage alimentaire'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Labels et certifications valorisés</h3>
                    <ul className="space-y-3">
                      {[
                        'Agriculture Biologique (AB)',
                        'Label Rouge, AOC, AOP, IGP',
                        'Bleu-Blanc-Cœur',
                        'MSC/ASC (produits de la mer)',
                        'Commerce équitable'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Alert Box */}
            <div className="mb-16 bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Calendrier : anticipez les renouvellements</h3>
                  <p className="text-slate-600">
                    Les marchés de restauration scolaire sont souvent publiés entre <strong>février et mai</strong> 
                    pour un démarrage en septembre. Les marchés hospitaliers et EHPAD peuvent être lancés 
                    tout au long de l'année. Durée moyenne : 3 à 4 ans.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-slate-900 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Développez votre activité avec les marchés de restauration collective
              </h2>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Recevez les appels d'offres restauration de votre région et accédez 
                à un marché en pleine croissance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700">
                    Créer mon compte gratuit
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/trouver-appels-offres">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    Voir les marchés du jour
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPath="/marches-publics-restauration" />

        <Footer />
      </div>
    </>
  );
}
